/**
 * Type for any data row with string keys
 */
type DataRow = Record<string, any>;

/**
 * Adds normalized range fields (0-1) for all numerical columns in the dataset
 *
 * @param data Array of data objects
 * @returns Array of data objects with added [columnName]_range fields
 */
export function addRangeFields<T extends DataRow>(
  data: T[],
  excludeColumn?: string,
  excludeValue?: any,
): Array<T & Record<string, number>> {
  if (data.length === 0) {
    return [];
  }

  // Identify numerical columns from all rows
  const numericColumns = new Set<string>();

  data.forEach(row => {
    Object.entries(row).forEach(([key, value]) => {
      if (typeof value === "number") {
        numericColumns.add(key);
      }
    });
  });

  const numericColumnArray = Array.from(numericColumns);

  // Calculate min and max for each numerical column
  const minValues: Record<string, number> = {};
  const maxValues: Record<string, number> = {};

  // Initialize min and max values
  numericColumnArray.forEach(column => {
    minValues[column] = Infinity;
    maxValues[column] = -Infinity;
  });

  // Find min and max across all rows, excluding specified values
  data.forEach(row => {
    if (excludeColumn && row[excludeColumn] === excludeValue) {
      // Skip this row
      return; // Skip this row
    }

    numericColumnArray.forEach(column => {
      if (typeof row[column] === "number") {
        if (row[column] < minValues[column]) {
          minValues[column] = row[column];
        }
        if (row[column] > maxValues[column]) {
          maxValues[column] = row[column];
        }
      }
    });
  });

  // Create a new array with added range fields
  return data.map(row => {
    // Create a separate object for range fields
    const rangeFields: Record<string, number> = {};

    numericColumnArray.forEach(column => {
      if (typeof row[column] === "number") {
        const min = minValues[column];
        const max = maxValues[column];

        // Handle the case where min and max are the same (to avoid division by zero)
        if (min === max) {
          rangeFields[`${column}_range`] = 0.5; // Middle of the range if there is no range
        } else {
          rangeFields[`${column}_range`] = (row[column] - min) / (max - min);
        }
      }
    });

    // Merge the original row with the range fields
    return { ...row, ...rangeFields };
  });
}

// Example usage with explicit type
// interface YourDataRow {
//   BidPriceBucket: string;
//   AvgBidPriceCpmMicroEur: number;
//   AvgImpValueMicroEur: number;
//   // ... other fields
// }

// Example usage:
// const originalData: YourDataRow[] = [...];
// const dataWithRanges = addRangeFields(originalData);
